import _ from 'lodash';

/**
 * Returns "includes substring" boolean result without case matching
 * @example
 * caseInsensitiveIncludes('AbCdFE', 'DFE')
 * // returns true
 * caseInsensitiveIncludes('AbCdFE', 'abcd')
 * // returns true
 * caseInsensitiveIncludes('AbCdFE', 'ABcD')
 * // returns true
 * caseInsensitiveIncludes('AbCdFE', 'abe')
 * // returns false
 * @param {string} target
 * @param {string} search
 * @returns {boolean}
 */
export const caseInsensitiveIncludes = (target, search) => {
  if (!_.isString(target)) {
    throw new Error(`Expected target type: string, got: ${typeof target}`);
  }

  if (!_.isString(search)) {
    throw new Error(`Expected search string type: string, got: ${typeof search}`);
  }

  return target.toLowerCase().includes(search.toLowerCase());
};

export const removeEscapeSequence = value => {
  if (!_.isString(value)) {
    return value;
  }

  return value.replace(/\n/g, '');
};

export const generateGroupWithNameValue = (group, name) => {
  return `${group} - ${name}`;
};

export const getValuesInsideCurlyBrackets = (string, includeGroupName = false) => {
  const regex = includeGroupName ? /{(.*?) - (.*?)}/gm : /\{(.*?)}/gm;

  return Array.from(string.matchAll(regex), ([__, group1, group2]) =>
    includeGroupName ? generateGroupWithNameValue(group1, group2) : group1,
  );
};

// Task types authored before the Tasks migration carry the SkynetCore DTO prefix; the current
// catalog publishes only the bare one. The backend render path accepts both (HZ-3928) — match here too.
const LEGACY_TASK_DYNAMIC_FIELD_PREFIX = 'TaskTypeDynamicFieldsDto.';
const TASK_DYNAMIC_FIELD_PREFIX = 'TaskTypeDynamicFields.';

export const replaceValuesInsideCurlyBrackets = ({
  string,
  options,
  keyToReplace,
  keyToReplaceWith,
  includeGroupName = false,
}) => {
  const optionsFromString = getValuesInsideCurlyBrackets(string);

  return optionsFromString.reduce(
    (acc, item) =>
      acc.replace(item, () => {
        const normalizedItem = item.startsWith(LEGACY_TASK_DYNAMIC_FIELD_PREFIX)
          ? item.replace(LEGACY_TASK_DYNAMIC_FIELD_PREFIX, TASK_DYNAMIC_FIELD_PREFIX)
          : item;
        const optionMatch = options.find(option => option?.[keyToReplace] === normalizedItem);

        if (!optionMatch) {
          return item;
        }

        const optionGroupName = optionMatch.group;
        const optionReplaceValue = optionMatch[keyToReplaceWith];

        return includeGroupName ? `${optionGroupName} - ${optionReplaceValue}` : optionReplaceValue;
      }),
    string,
  );
};

export const removeExcessiveSpaces = str => (str || '').replace(/\s{2,}/g, ' ').trim();

export const startCasePreserveSymbols = str =>
  _.isString(str) ? str.replace(/\w+/g, _.capitalize) : str;

export const pascalCaseToStartCase = str => {
  return _.isString(str) ? str.replace(/([a-z])(?=[A-Z])/g, '$1 ') : str;
};

export const getShortenString = (string, length) => {
  if (string.length <= length) {
    return string;
  }

  return `${string.slice(0, length)}...`;
};

export const capitalizeFirstLetterInNameField = string =>
  string ? string.replace(/(\s|^|\u2018|\u2019|')([a-z])/g, i => i.toUpperCase()) : string;
